<template>
  <Transition name="toast" appear :style="{ '--transition-duration': TRANSITION_DURATION + 'ms' }">
    <div v-if="visible" :class="`toast-container toast-container--${config.type || 'info'}`">
      <div class="toast-content">
        <div class="toast-message">{{ config.message }}</div>
        <div class="toast-tip" v-if="config.tip">{{ config.tip }}</div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { reactive, ref, onBeforeUnmount } from 'vue'
import type { ToastConfig } from '@/types/dialog'
// 卸载动画时长
const TRANSITION_DURATION = 300

// 响应式状态
const visible = ref(false)

const config = reactive<ToastConfig>({
  message: '',
  tip: '',
  type: 'info',
  duration: 2000
})

let timer: NodeJS.Timeout | null = null
let closeTimer: NodeJS.Timeout | null = null

// 方法
const show = (options: ToastConfig) => {
  Object.assign(config, {
    type: 'info',
    ...options
  })

  visible.value = true

  // 设置自动关闭
  if (config.duration && config.duration > 0) {
    timer = setTimeout(() => {
      hide()
    }, config.duration)
  }
}

const hide = () => {
  visible.value = false
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  // 清除之前的 closeTimer，避免多次调用 hide() 时创建多个定时器
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
  // 延迟调用 onClose，等待离开动画完成
  closeTimer = setTimeout(() => {
    closeTimer = null
    config.onClose?.()
  }, TRANSITION_DURATION)
}

// 组件卸载时清理所有定时器
onBeforeUnmount(() => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
})

// 暴露方法
defineExpose({
  show,
  hide
})
</script>

<style lang="scss" scoped>
// 变量定义
$toast-border-radius: 12px;
$toast-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

$toast-backdrop-blur: blur(2px);
// 背景色
$bg-color: rgba(0, 0, 0, 0.5);
// 颜色变量

.toast-container {
  position: fixed;
  z-index: 9999;

  gap: 8px;
  padding: 16px 60px 12px;
  border-radius: $toast-border-radius;
  background: $bg-color;
  color: white;
  backdrop-filter: $toast-backdrop-blur;
  max-width: 400px;
  min-width: 120px;
  text-align: center;
  box-shadow: $toast-shadow;
  border: 0.5px solid #ff9d47;

  /* 居中位置 */
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 12px;
  background: $bg-color;
}

.toast-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.toast-message {
  font-size: 15px;
  line-height: 1.4;
  word-wrap: break-word;
  white-space: pre-wrap;
  color: #ffffff;
  font-weight: 500;
}
.toast-tip {
  color: #eeeeee;
  font-size: 14px;
  line-height: 1.4;
}

.toast-loading {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Vue Transition 动画
.toast-enter-active,
.toast-leave-active {
  transition: all var(--transition-duration) ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translate(-50%, -60%) scale(0.9);
}

.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -60%) scale(0.9);
}
</style>
